import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { API } from '../config/apiBase';
import useImpressao from '../inteligencia/useImpressao';
import { registrar } from '../inteligencia/coleta';
import { ehAmazonManual } from '../utils/amazonSitestripe';

// Base do encurtador/tracker. Em prod cai pra https://ir.supervitrineofertas.com.
const IR_BASE = process.env.REACT_APP_IR_BASE_URL || 'https://ir.supervitrineofertas.com';

// O clique de banner SEMPRE passa por /b/<banner_id> no redirector: é lá que o
// clique é registrado e o link de afiliado é decidido. O visual pode mudar à
// vontade; este destino não.
function trackedHref(banner) {
    if (!banner || !banner.banner_id) return banner?.link || '#';
    return `${IR_BASE}/b/${banner.banner_id}`;
}

function resolveImg(url) {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const base = (API || '').replace(/\/api\/?$/, '');
    return `${base}${url}`;
}

// Uma busca dos banners de hero por visita, compartilhada entre o hero e a faixa
// de destaques, que precisa saber o que já está no topo para não repetir.
let _heroEmVoo = null;
export function carregarBannersHero() {
    if (!_heroEmVoo) {
        _heroEmVoo = fetch(`${API}/banners?position=hero`)
            .then((r) => r.json())
            .then((d) => (Array.isArray(d) ? d : []))
            .catch(() => []);
    }
    return _heroEmVoo;
}
const chaveOferta = (b) => b?.source_deal_id || b?.banner_id;

const reduzMovimento = () => typeof window !== 'undefined'
    && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Autoplay decidido pela auditoria: uma referência troca a cada 6 s, a outra a
// cada 5 s. Desktop fica em 6 s; no celular, 7 s, para não apressar quem lê.
const intervaloAutoplay = () => (typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(max-width: 639px)').matches ? 7000 : 6000);

const moeda = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

/**
 * Banner gerado automaticamente a partir de uma oferta.
 *
 * O backend também gera uma arte pronta para esses banners, mas ela carrega a
 * identidade visual anterior (preto e dourado) embutida na imagem. Aqui o banner
 * é desenhado com os próprios dados que ele já traz — título, loja, desconto e
 * foto do produto — para fazer parte da vitrine em vez de parecer colado nela.
 * Banner cadastrado à mão continua mostrando a arte enviada pelo admin.
 */
const ehAutomatico = (b) => b?.source === 'auto' && !!b?.product_image_url;

// Celular (< 640 px): texto à esquerda e foto à direita, numa faixa de 250 px.
// Antes a foto ficava em cima e o texto embaixo, em 360 px de altura: o hero
// ocupava quase a primeira tela inteira. A partir de `sm` nada muda.
function SlideAutomatico({ banner }) {
    return (
        <div className="absolute inset-0 flex flex-row" data-testid="slide-automatico" style={{
            background: 'radial-gradient(640px 360px at 78% 45%, rgba(70,140,255,0.45), transparent 65%), linear-gradient(115deg, #0A2A66 0%, #1147A8 60%, #1756B8 100%)',
        }}>
            <div className="sv-hero-texto relative z-10 flex flex-col justify-center gap-2.5 sm:gap-3 py-5 pl-5 pr-2 sm:p-9 lg:p-12 w-[57%] sm:w-[58%] min-w-0">
                {banner.store_name && (
                    <span className="self-start text-[11px] sm:text-[12px] font-bold px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.12)', color: '#fff' }}>
                        {banner.store_name}
                    </span>
                )}
                <h2 className="sv-hero-titulo text-[17px] min-[380px]:text-[18px] sm:text-[28px] lg:text-[34px] font-extrabold leading-[1.16] sm:leading-[1.08] line-clamp-4 sm:line-clamp-3"
                    style={{ color: '#fff', fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
                    {banner.title}
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="sv-btn-action max-sm:!px-4 max-sm:!py-2.5 max-sm:!text-[14px]">Ver oferta</span>
                </div>
            </div>
            <div className="relative flex-1 flex items-center justify-center py-4 pr-4 pl-1 sm:p-8">
                <div className="sv-hero-produto relative w-[118px] h-[118px] min-[380px]:w-[132px] min-[380px]:h-[132px] sm:w-[240px] sm:h-[240px] lg:w-[280px] lg:h-[280px] rounded-[18px] sm:rounded-[22px] flex items-center justify-center"
                    style={{ background: 'var(--sv-media)' }}>
                    <img src={resolveImg(banner.product_image_url)} alt="" className="w-[82%] h-[82%] object-contain" style={{ mixBlendMode: 'multiply' }} />
                    {Number(banner.discount_percent) > 0 && (
                        <span className="sv-off absolute -top-2.5 -left-2.5 sm:-top-4 sm:-left-4 !text-[14px] sm:!text-[24px] !px-2 !py-1.5 sm:!px-3 sm:!py-2 !rounded-lg sm:!rounded-xl"
                            style={{ boxShadow: '0 10px 24px -10px rgba(224,45,75,0.9)' }}>
                            -{banner.discount_percent}%
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function SlideArte({ banner }) {
    return (
        <>
            <img src={resolveImg(banner.image_url)} alt={banner.title || 'Banner'} className="sv-hero-arte absolute inset-0 w-full h-full object-cover" />
            {(banner.title || banner.store_name) && (
                <>
                    <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(90deg, rgba(5,20,50,0.85) 0%, rgba(5,20,50,0.25) 55%, transparent 100%)' }} />
                    <div className="sv-hero-texto absolute bottom-6 left-6 right-6 sm:bottom-10 sm:left-10 max-w-[560px]">
                        {banner.store_name && <span className="text-[12px] font-bold" style={{ color: 'var(--gold-1)' }}>{banner.store_name}</span>}
                        {banner.title && (
                            <h2 className="text-[22px] md:text-[30px] font-extrabold mt-1 line-clamp-2" style={{ color: '#fff', fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
                                {banner.title}
                            </h2>
                        )}
                    </div>
                </>
            )}
        </>
    );
}

const ehDesktop = () => typeof window !== 'undefined'
    && (!window.matchMedia || window.matchMedia('(min-width: 1024px)').matches);

/**
 * Coluna ao lado do hero, só no desktop: as duas ofertas que acabaram de entrar.
 *
 * `recentes` vem da Home quando o feed abre em "Recentes": é a mesma consulta, e
 * a coluna reaproveita a resposta em vez de repetir a chamada. `null` quer dizer
 * que o feed ainda está carregando. Sem a prop (feed aberto em outra ordem), a
 * coluna busca sozinha, e só no desktop, onde ela aparece.
 */
function ChegaramAgora({ recentes }) {
    const [buscados, setBuscados] = useState(null);
    const usaDaHome = recentes !== undefined;
    useEffect(() => {
        if (usaDaHome || !ehDesktop()) return undefined;
        let ativo = true;
        fetch(`${API}/produtos?sort=recent&limit=8`)
            .then((r) => r.json())
            .then((d) => {
                if (ativo) setBuscados(Array.isArray(d?.deals) ? d.deals : []);
            })
            .catch(() => { if (ativo) setBuscados([]); });
        return () => { ativo = false; };
    }, [usaDaHome]);

    const origem = usaDaHome ? recentes : buscados;
    // Destaque só para oferta com destino de compra (campo do backend). A oferta Amazon manual (SiteStripe) não
    // tem preço nem imagem da Amazon: o card daqui imprime preço ("R$ NaN") e foto, então ela fica de fora.
    const deals = Array.isArray(origem)
        ? origem.filter((x) => x.purchase_available !== false && !ehAmazonManual(x)).slice(0, 2) : null;

    if (deals && deals.length === 0) return null;

    return (
        // Altura vem do hero, não do conteúdo: título em linha `auto` e os dois
        // cards dividindo o resto em partes iguais (.sv-hero-lateral no index.css).
        <aside className="sv-hero-lateral hidden lg:grid" aria-label="Ofertas que acabaram de chegar" data-testid="hero-lateral">
            <p className="text-[14px] font-bold" style={{ color: 'var(--muted)' }}>Acabaram de chegar</p>
            {(deals || [null, null]).map((d, i) => (d
                ? <CardChegou key={d.deal_id} d={d} posicao={i + 1} />
                : <div key={i} className="sv-skeleton rounded-2xl min-h-0" aria-hidden="true" />))}
        </aside>
    );
}

/** Card da coluna lateral do hero. Superfície `home_hero` para a coleta da inteligência. */
function CardChegou({ d, posicao }) {
    const exposicao = { surface: 'home_hero', position: posicao, section: 'hero-lateral' };
    const ref = useImpressao(d, exposicao);
    return (
        <Link to={`/oferta/${d.deal_id}`} ref={ref}
            onClick={() => registrar('detail_click', { deal_id: d.deal_id, ...exposicao, marketplace: d.store_name || '' })}
            className="sv-card sv-card--row sv-card--lateral group flex flex-row items-center gap-4 p-3 min-h-0 overflow-hidden"
            data-testid={`hero-recente-${d.deal_id}`}>
            <div className="sv-card-media !m-0 w-[120px] h-[120px] max-h-full shrink-0 flex items-center justify-center">
                <img src={d.image_url} alt={d.title} loading="lazy" className="w-full h-full object-contain p-2" />
                {d.discount_percent > 0 && <span className="sv-off absolute top-1.5 left-1.5">-{d.discount_percent}%</span>}
            </div>
            <div className="min-w-0 max-h-full flex flex-col gap-1.5">
                <span className="text-[12px] font-semibold" style={{ color: 'var(--muted)' }}>{d.store_name}</span>
                <p className="text-[14px] font-semibold line-clamp-2 leading-snug" style={{ color: 'var(--text)' }}>{d.title}</p>
                <span className="text-[18px] font-extrabold sv-num truncate" style={{ color: 'var(--text)' }}>{moeda(d.current_price)}</span>
            </div>
        </Link>
    );
}

/** Mesma altura e grade do hero pronto: a troca não empurra a página. */
export const EsqueletoHero = () => (
    <div className="grid lg:grid-cols-[minmax(0,2.1fr)_minmax(0,1fr)] gap-x-5" aria-busy="true" aria-label="Carregando destaques" data-testid="hero-esqueleto">
        <div className="sv-skeleton w-full h-[250px] sm:h-[340px] lg:h-[380px] rounded-[22px]" />
        <div className="hidden lg:block sv-skeleton rounded-[22px]" />
        {/* Mesma linha dos indicadores do hero pronto: a troca não empurra a página. */}
        <div className="sv-hero-dots-espaco mt-3 h-1.5 lg:col-start-1" aria-hidden="true" />
    </div>
);

/**
 * Carrossel de banners do hero.
 *
 * Não decide sozinho se deve aparecer: quem decide entre banners e campanhas da
 * Central é a Home (`vitrine/decisaoHero.js`), antes de montar este componente.
 * Assim ele nunca chega a desenhar um banner que sairia de cena em seguida.
 */
export const BannerCarousel = ({ recentes }) => {
    const [banners, setBanners] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    // Slide que acabou de sair e sentido da troca (1 = próximo, -1 = anterior):
    // o que sai fica parado embaixo e o que entra desliza um pouco por cima.
    const [anterior, setAnterior] = useState(null);
    const [direcao, setDirecao] = useState(1);
    const [animado, setAnimado] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pausado, setPausado] = useState(false);
    const indiceRef = useRef(0);
    const toqueRef = useRef(null);

    useEffect(() => {
        let ativo = true;
        carregarBannersHero()
            .then((data) => { if (ativo) setBanners(data); })
            .finally(() => { if (ativo) setLoading(false); });
        return () => { ativo = false; };
    }, []);

    const irPara = useCallback((destino, sentido) => {
        const total = banners.length;
        if (total < 2) return;
        const atual = indiceRef.current;
        const proximo = ((destino % total) + total) % total;
        if (proximo === atual) return;
        indiceRef.current = proximo;
        setAnterior(atual);
        setAnimado(true);
        setDirecao(sentido || (proximo > atual ? 1 : -1));
        setCurrentIndex(proximo);
    }, [banners.length]);

    const nextSlide = useCallback(() => irPara(indiceRef.current + 1, 1), [irPara]);
    const prevSlide = useCallback(() => irPara(indiceRef.current - 1, -1), [irPara]);

    // Um timer por slide: qualquer troca manual (seta, indicador, gesto) recomeça
    // a contagem, e o indicador de progresso recomeça junto.
    useEffect(() => {
        if (banners.length <= 1 || pausado || reduzMovimento()) return undefined;
        const timer = setTimeout(nextSlide, intervaloAutoplay());
        return () => clearTimeout(timer);
    }, [banners.length, currentIndex, nextSlide, pausado]);

    // Mouse pausa; toque não (senão o celular ficaria pausado para sempre).
    const aoEntrar = (e) => { if (e.pointerType === 'mouse') setPausado(true); };
    const aoSair = (e) => { if (e.pointerType === 'mouse') setPausado(false); };

    const aoTocar = (e) => {
        const t = e.touches[0];
        toqueRef.current = { x: t.clientX, y: t.clientY };
        setPausado(true);
    };
    const aoSoltar = (e) => {
        const inicio = toqueRef.current;
        toqueRef.current = null;
        setPausado(false);
        if (!inicio) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - inicio.x;
        const dy = t.clientY - inicio.y;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
            if (dx < 0) nextSlide(); else prevSlide();
        }
    };

    const aoTeclar = (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); nextSlide(); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); prevSlide(); }
    };

    if (loading) return <EsqueletoHero />;
    if (banners.length === 0) return null;

    const rodando = banners.length > 1 && !pausado && !reduzMovimento();
    const intervalo = intervaloAutoplay();

    return (
        // Desktop: grid de 2 colunas e 2 linhas. Linha 1 = hero + coluna lateral;
        // linha 2 = indicadores, só embaixo do hero. Assim a coluna lateral estica
        // até a base do hero e não até a base dos indicadores (ver .sv-hero-bloco).
        <div className="sv-hero-bloco grid lg:grid-cols-[minmax(0,2.1fr)_minmax(0,1fr)] gap-x-5" data-testid="banner-carousel">
            <div
                className={`sv-hero ${animado ? 'is-animado' : ''} relative w-full h-[250px] sm:h-[340px] lg:h-[380px] rounded-[22px] overflow-hidden`}
                style={{ '--sv-hero-dir': direcao }}
                onPointerEnter={aoEntrar}
                onPointerLeave={aoSair}
                onFocus={() => setPausado(true)}
                onBlur={() => setPausado(false)}
                onTouchStart={aoTocar}
                onTouchEnd={aoSoltar}
                onTouchCancel={() => { toqueRef.current = null; setPausado(false); }}
                onKeyDown={aoTeclar}
                aria-roledescription="carrossel"
                aria-label="Ofertas em destaque"
            >
                {banners.map((banner, index) => (
                    <a
                        key={banner.banner_id}
                        href={trackedHref(banner)}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        data-testid={`hero-banner-${banner.banner_id}`}
                        className={`sv-hero-slide absolute inset-0 ${index === currentIndex ? 'is-active' : ''} ${index === anterior && index !== currentIndex ? 'is-leaving' : ''}`}
                        aria-hidden={index !== currentIndex}
                        tabIndex={index === currentIndex ? 0 : -1}
                    >
                        {ehAutomatico(banner) ? <SlideAutomatico banner={banner} /> : <SlideArte banner={banner} />}
                    </a>
                ))}

                {banners.length > 1 && (
                    <>
                        <button onClick={prevSlide} aria-label="Banner anterior"
                            className="sv-hero-seta hidden sm:flex absolute left-3 top-1/2 z-20 w-10 h-10 rounded-full items-center justify-center"
                            data-testid="carousel-prev">
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button onClick={nextSlide} aria-label="Próximo banner"
                            className="sv-hero-seta hidden sm:flex absolute right-3 top-1/2 z-20 w-10 h-10 rounded-full items-center justify-center"
                            data-testid="carousel-next">
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </>
                )}
            </div>

            {banners.length > 1 && (
                // A linha continua com 6 px de altura, como antes; a área de
                // clique de cada indicador é maior e transborda para cima e para baixo.
                <div className="sv-hero-dots flex items-center justify-center gap-1 mt-3 h-1.5" role="tablist" aria-label="Escolher banner"
                    data-testid="carousel-dots">
                    {banners.map((b, index) => {
                        const ativo = index === currentIndex;
                        return (
                            <button key={b.banner_id} type="button" role="tab" aria-selected={ativo}
                                onClick={() => irPara(index)}
                                className={`sv-hero-dot ${ativo ? 'is-active' : ''} ${ativo && rodando ? 'is-rodando' : ''}`}
                                aria-label={`Banner ${index + 1} de ${banners.length}`}
                                data-testid={`carousel-dot-${index}`}>
                                <span className="sv-hero-dot-barra" aria-hidden="true">
                                    {ativo && rodando && (
                                        <span key={`${currentIndex}-${pausado}`} className="sv-hero-dot-progresso" style={{ animationDuration: `${intervalo}ms` }} />
                                    )}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            <ChegaramAgora recentes={recentes} />
        </div>
    );
};

export const SidebarBanner = () => {
    const [banners, setBanners] = useState([]);
    useEffect(() => {
        fetch(`${API}/banners?position=sidebar`).then(r => r.json()).then(d => setBanners(Array.isArray(d) ? d : [])).catch(() => {});
    }, []);
    if (banners.length === 0) return null;
    return (
        <div className="space-y-5" data-testid="sidebar-banners">
            {banners.map((banner) => (
                <a key={banner.banner_id} href={trackedHref(banner)} target="_blank" rel="noopener noreferrer sponsored"
                    className="sv-card block overflow-hidden mx-auto" data-testid={`sidebar-banner-${banner.banner_id}`}>
                    <img src={resolveImg(banner.image_url)} alt={banner.title} className="w-full h-auto object-cover block" />
                </a>
            ))}
        </div>
    );
};

// Banner como célula do grid de ofertas. Ocupa a mesma altura de um card.
export const GridBannerCard = ({ banner }) => {
    if (!banner) return null;
    const auto = ehAutomatico(banner);
    return (
        <a
            href={trackedHref(banner)}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className={`sv-card group flex flex-col h-full overflow-hidden ${auto ? 'sv-faixa-card sv-faixa-card--grid' : ''}`}
            data-testid={`grid-banner-${banner.banner_id}`}
        >
            {auto ? (
                <div className="flex flex-col h-full p-4 gap-3">
                    <div className="flex items-center justify-between">
                        <span className="text-[12px] font-bold" style={{ color: 'var(--gold-1)' }}>{banner.store_name}</span>
                        <span className="sv-anuncio-selo text-[11px] font-semibold px-1.5 py-0.5 rounded">Anúncio</span>
                    </div>
                    <div className="sv-card-media !m-0 relative flex-1 min-h-[140px] flex items-center justify-center">
                        <img src={resolveImg(banner.product_image_url)} alt="" loading="lazy" className="w-[80%] h-[80%] object-contain" />
                        {Number(banner.discount_percent) > 0 && (
                            <span className="sv-off absolute top-2 left-2 !text-[15px] !px-2.5 !py-1.5">-{banner.discount_percent}%</span>
                        )}
                    </div>
                    <p className="sv-faixa-titulo text-[15px] font-extrabold leading-snug line-clamp-3" style={{ fontFamily: 'var(--font-display)' }}>{banner.title}</p>
                    <span className="sv-card-cta !bg-[var(--sv-action)] !text-[var(--sv-on-action)] !border-transparent">Ver oferta</span>
                </div>
            ) : (
                <div className="aspect-[3/4] w-full relative overflow-hidden">
                    <img src={resolveImg(banner.image_url)} alt={banner.title || 'Anúncio'}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                    {banner.store_name && (
                        <span className="absolute top-2 left-2 text-[11px] font-bold px-2 py-0.5 rounded" style={{ background: 'rgba(5,20,50,0.7)', color: '#fff' }}>
                            {banner.store_name}
                        </span>
                    )}
                    <span className="absolute bottom-2 right-2 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: 'rgba(5,20,50,0.7)', color: '#fff' }}>
                        Anúncio
                    </span>
                </div>
            )}
        </a>
    );
};

/**
 * Faixa de destaques entre as seções da Home.
 *
 * Quebra o ritmo das grades com um formato diferente: cartões largos, foto à
 * esquerda e ação à direita. Usa só os banners gerados a partir de ofertas
 * reais; os banners cadastrados à mão com imagem genérica ficam de fora daqui,
 * mas continuam aparecendo onde já apareciam, no meio do feed.
 *
 * O clique continua indo por /b/<banner_id>, como qualquer banner.
 */
export const FaixaDestaques = ({ banners = [] }) => {
    const [noHero, setNoHero] = useState(null);
    useEffect(() => {
        let ativo = true;
        carregarBannersHero().then((h) => { if (ativo) setNoHero(new Set(h.map(chaveOferta))); });
        return () => { ativo = false; };
    }, []);
    if (noHero === null) return null;
    const itens = banners.filter((b) => ehAutomatico(b) && !noHero.has(chaveOferta(b))).slice(0, 3);
    // Um cartão sozinho não forma faixa; vira só um card perdido.
    if (itens.length < 2) return null;
    const colunas = itens.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-3';
    return (
        <section className="sv-section" data-testid="faixa-destaques" aria-labelledby="faixa-destaques-titulo">
            <div className="sv-section-head">
                <div className="min-w-0">
                    <h2 className="sv-h2" id="faixa-destaques-titulo">Em destaque hoje</h2>
                    <p className="sv-sub">Ofertas escolhidas para ganhar vitrine própria.</p>
                </div>
            </div>
            <div className={`sv-rail flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-2 lg:mx-0 lg:px-0 lg:pb-0 lg:grid ${colunas} lg:overflow-visible`}>
                {itens.map((b) => (
                    <a key={b.banner_id} href={trackedHref(b)} target="_blank" rel="noopener noreferrer sponsored"
                        className="sv-card sv-card--row sv-faixa-card group shrink-0 snap-start w-[86%] sm:w-[420px] lg:w-auto flex flex-row items-center gap-4 p-4 overflow-hidden"
                        data-testid={`faixa-banner-${b.banner_id}`}>
                        <div className="relative w-[112px] h-[112px] shrink-0 rounded-2xl flex items-center justify-center" style={{ background: 'var(--sv-media)' }}>
                            <img src={resolveImg(b.product_image_url)} alt="" loading="lazy" className="w-[84%] h-[84%] object-contain" style={{ mixBlendMode: 'multiply' }} />
                            {Number(b.discount_percent) > 0 && (
                                <span className="sv-off absolute -top-2 -left-2 !text-[14px] !px-2 !py-1.5">-{b.discount_percent}%</span>
                            )}
                        </div>
                        <div className="min-w-0 flex flex-col gap-2">
                            <span className="text-[12px] font-bold" style={{ color: 'var(--gold-1)' }}>{b.store_name}</span>
                            <p className="sv-faixa-titulo text-[16px] font-extrabold leading-snug line-clamp-2" style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }}>{b.title}</p>
                            <span className="sv-btn-action self-start !px-3.5 !py-2 !text-[13px]">Ver oferta</span>
                        </div>
                    </a>
                ))}
            </div>
        </section>
    );
};

// Fetch helper exportado pra paginas que querem intercalar banners no grid.
export async function fetchSidebarBanners() {
    try {
        const r = await fetch(`${API}/banners?position=sidebar`);
        const d = await r.json();
        return Array.isArray(d) ? d : [];
    } catch { return []; }
}

// Banner inline pra intercalar entre seções. Faz fetch uma vez e mantém cache em
// módulo pra evitar requisições repetidas. Some quando não há banner ativo.
let _inlineCache = null;
let _inlineFetchPromise = null;
async function _fetchInline() {
    if (_inlineCache) return _inlineCache;
    if (_inlineFetchPromise) return _inlineFetchPromise;
    _inlineFetchPromise = fetch(`${API}/banners?position=inline`)
        .then(r => r.json())
        .then(d => { _inlineCache = Array.isArray(d) ? d : []; return _inlineCache; })
        .catch(() => { _inlineCache = []; return []; });
    return _inlineFetchPromise;
}

export const InlineBanner = ({ rotationIndex = 0 }) => {
    const [banner, setBanner] = useState(null);
    useEffect(() => {
        let mounted = true;
        _fetchInline().then(arr => {
            if (!mounted || !arr.length) return;
            setBanner(arr[rotationIndex % arr.length]);
        });
        return () => { mounted = false; };
    }, [rotationIndex]);
    if (!banner) return null;
    return (
        <a
            href={trackedHref(banner)}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="sv-card sv-section block overflow-hidden"
            style={{ maxHeight: '200px' }}
            data-testid={`inline-banner-${banner.banner_id}`}
        >
            <img src={resolveImg(banner.image_url)} alt={banner.title || 'Oferta'} className="w-full object-cover" style={{ maxHeight: '200px' }} loading="lazy" />
        </a>
    );
};
